import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import LanguageClassesManager from '../components/LanguageClassesManager';
import ProfileCompletionForm from '../components/ProfileCompletionForm';
import NotificationBell from '../components/NotificationBell';
import { useLanguage } from '../context/LanguageContext';
import { calculateProfileCompletion } from '../utils/profileCompletion';
import {
  EditPersonalDetailsModal,
  EditAddressModal,
  EditNationalityModal,
  EditLanguageModal,
  AddEmploymentModal,
  AddEducationModal,
  AddCertificateModal,
  AddDocumentModal,
  AddNoteModal,
  AddSkillModal
} from '../components/profile';
import {
  LayoutDashboard,
  User,
  FileText,
  Settings,
  LogOut,
  GraduationCap,
  Briefcase,
  Mail,
  Phone,
  Calendar,
  Award,
  BookOpen,
  ChevronRight,
  Eye,
  EyeOff,
  Bell,
  X,
  XCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  Languages,
  Check,
  CheckCheck,
  MessageSquare,
  Send,
  Edit2,
  Plus,
  MapPin,
  Globe,
  Download,
  Trash2,
  ShieldCheck,
  Menu,
  Video,
  MoreHorizontal
} from 'lucide-react';
import { generateProfilePDF } from '../utils/generateProfilePDF';


const PIPELINE_STAGES = [
  'Interview waiting', 'Interview Scheduled', 'Interview attended',
  'Offer Received', 'Visa submitted', 'Visa Granted', 'Migrated'
];

const STAGE_COLORS = {
  'Interview waiting': { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-700' },
  'Interview Scheduled': { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700' },
  'Interview attended': { bg: 'bg-teal-500', light: 'bg-teal-50', text: 'text-teal-700' },
  'Offer Received': { bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-700' },
  'Visa submitted': { bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-700' },
  'Visa Granted': { bg: 'bg-purple-500', light: 'bg-purple-50', text: 'text-purple-700' },
  'Migrated': { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-700' },
};

const ApplicationStatusStepper = ({ currentStage }) => {
  const currentIndex = PIPELINE_STAGES.indexOf(currentStage);
  const activeIndex = currentIndex >= 0 ? currentIndex : 0;

  return (
    <div className="w-full py-4 overflow-x-auto no-scrollbar">
      <div className="flex items-start justify-between min-w-[700px] px-2">
        {PIPELINE_STAGES.map((stage, index) => {
          const isCompleted = index < activeIndex;
          const isActive = index === activeIndex;
          const colors = STAGE_COLORS[stage] || {};

          return (
            <div key={stage} className="flex flex-col items-center flex-1 relative">
              {index < PIPELINE_STAGES.length - 1 && (
                <div className={`absolute top-4 left-1/2 w-full h-[2px] z-0 ${index < activeIndex ? 'bg-cyan-500' : 'bg-slate-200'}`} />
              )}
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center relative z-10 transition-all duration-500
                ${isCompleted ? `${colors.bg} text-white shadow-lg` :
                  isActive ? 'bg-white border-2 border-cyan-600 text-cyan-600 shadow-xl scale-110' :
                    'bg-white border-2 border-slate-200 text-slate-400'}
              `}>
                {isCompleted ? <Check className="h-4 w-4" /> : <span className="text-[8px] font-bold">{index + 1}</span>}
              </div>
              <div className="mt-2 text-center px-0.5">
                <p className={`text-[8px] font-bold uppercase tracking-wider leading-tight ${isActive ? 'text-cyan-600' : isCompleted ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {stage}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Compact SidebarItem for consistency
const SidebarItem = ({ icon: Icon, label, active, onClick, badge, hasSubmenu, isOpen }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${active
      ? 'bg-cyan-50 text-cyan-700 font-semibold'
      : 'text-slate-600 hover:bg-slate-50 hover:text-cyan-600'
      }`}
  >
    <div className="flex items-center gap-3 relative z-10 w-full">
      <div className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-cyan-100/50' : 'bg-transparent group-hover:bg-cyan-50'
        }`}>
        <Icon className={`h-4 w-4 ${active ? 'text-cyan-600' : 'text-slate-400 group-hover:text-cyan-500'}`} />
      </div>
      <span className="text-sm truncate flex-1 text-left">{label}</span>
      {badge && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500'
          }`}>
          {badge}
        </span>
      )}
      {hasSubmenu && (
        <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''
          } ${active ? 'text-cyan-600' : 'text-slate-400'}`} />
      )}
    </div>
  </button>
);

const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, logout } = useAuth();
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/applications') || path.includes('/interviews')) return 'interviews';
    if (path.includes('/language-classes')) return 'language-classes';
    if (path.includes('/settings')) return 'settings';
    return 'overview';
  };
  const activeTab = getActiveTab();

  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [rejectedApplications, setRejectedApplications] = useState([]);
  const [interviewsSubTab, setInterviewsSubTab] = useState('applications'); // 'applications' | 'scheduled'

  // Profile edit modal states
  const [editingSection, setEditingSection] = useState(null); // 'personal' | 'address' | 'nationality' | 'language'
  const [editingLanguageIndex, setEditingLanguageIndex] = useState(null); // null for new, index for editing
  const [languageProfiles, setLanguageProfiles] = useState([]);

  // Profile sub-tabs and Employment/Education states
  const [profileSubTab, setProfileSubTab] = useState('profile'); // 'profile' | 'employment' | 'skills' | 'documents' | 'notes'
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [editingEmploymentIndex, setEditingEmploymentIndex] = useState(null);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [editingEducationIndex, setEditingEducationIndex] = useState(null);
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [educationHistory, setEducationHistory] = useState([]);

  // Skills, Documents, and Notes states
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [editingCertificateIndex, setEditingCertificateIndex] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingDocumentIndex, setEditingDocumentIndex] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  const [showSkillModal, setShowSkillModal] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Helper function to get photo URL
  const getPhotoUrl = (photoUrl) => {
    if (!photoUrl) return null;

    // If it's already a data URI (Base64), return as is
    if (photoUrl.startsWith('data:')) return photoUrl;

    let baseUrl = process.env.REACT_APP_API_URL;
    if (!baseUrl) {
      const origin = window.location.origin;
      baseUrl = origin.replace(':3000', ':8080');
    }
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    } else if (baseUrl.includes('/api/')) {
      baseUrl = baseUrl.replace('/api/', '/');
    } else if (baseUrl.endsWith('/api/')) {
      baseUrl = baseUrl.slice(0, -5);
    }
    baseUrl = baseUrl.replace(/\/$/, '');
    const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
    const timestamp = new Date().getTime();
    return `${baseUrl}${normalizedPhotoUrl}?t=${timestamp}`;
  };

  const handleViewDocument = (dataUri) => {
    if (!dataUri) return;

    // For legacy URLs or non-base64, use getPhotoUrl and standard window.open
    if (!dataUri.startsWith('data:')) {
      window.open(getPhotoUrl(dataUri), '_blank');
      return;
    }

    try {
      // Convert data URI to Blob
      const parts = dataUri.split(';base64,');
      if (parts.length !== 2) {
        window.open(dataUri, '_blank');
        return;
      }

      const contentType = parts[0].split(':')[1];
      const byteCharacters = atob(parts[1]);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);

      window.open(blobUrl, '_blank');

      // Note: We don't revokeObjectURL here because the new tab needs it.
      // In a long-running SPA, this could be a minor leak if many docs are opened,
      // but it's the standard way to handle this.
    } catch (e) {
      console.error('Error viewing document:', e);
      window.open(dataUri, '_blank');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchNotifications();
    fetchJobApplications();
    fetchInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to dashboard if on base student path
  useEffect(() => {
    if (location.pathname === '/student' || location.pathname === '/student/') {
      navigate('/student/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);



  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/student/dashboard');
      const data = response.data || {};

      // Ensure all JSON fields are parsed if they come as strings
      const jsonFields = ['skills', 'education_history', 'employment_history', 'certificates', 'documents', 'language_certificates', 'notes'];

      const parseField = (obj, field) => {
        if (obj && obj[field] && typeof obj[field] === 'string') {
          try {
            obj[field] = JSON.parse(obj[field]);
          } catch (e) {
            console.warn(`Failed to parse ${field}:`, e);
            obj[field] = [];
          }
        } else if (obj && !obj[field]) {
          obj[field] = [];
        }
      };

      jsonFields.forEach(field => {
        parseField(data, field);
        if (data.user) {
          parseField(data.user, field);
        }
      });

      setDashboardData(data);
    } catch (error) {
      console.error('[StudentDashboard] Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviews = async () => {
    try {
      const response = await api.get('/student/interviews');
      setInterviews(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      setInterviews([]);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/student/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchJobApplications = async () => {
    try {
      const response = await api.get('/student/job-applications');
      setJobApplications(response.data?.applications || []);
      setRejectedApplications(response.data?.rejected || []);
    } catch (error) {
      console.error('Error fetching job applications:', error);
    }
  };



  const handleProfileSaved = async () => {
    try {
      const response = await api.get('/student/dashboard');
      if (response.data?.skills) {
        if (typeof response.data.skills === 'string') {
          try {
            response.data.skills = JSON.parse(response.data.skills);
          } catch (e) {
            response.data.skills = [];
          }
        }
      }
      setDashboardData(response.data);
      setIsEditingProfile(false);
    } catch (error) {
      console.error('[StudentDashboard] Error in handleProfileSaved:', error);
    }
  };

  // Handle saving section updates (Personal, Address, Nationality)
  const handleSectionSave = async (formData) => {
    try {
      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });
      await handleProfileSaved();
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving profile section:', error);
      throw error;
    }
  };

  // Handle saving language updates
  const handleLanguageSave = async (languageEntry) => {
    try {
      // Get current language certificates
      const currentLangs = dashboardData?.language_certificates || [];
      let updatedLangs;

      if (editingLanguageIndex !== null) {
        // Editing existing language
        updatedLangs = [...currentLangs];
        updatedLangs[editingLanguageIndex] = languageEntry;
      } else {
        // Adding new language
        updatedLangs = [...currentLangs, languageEntry];
      }

      const formData = new FormData();
      formData.append('language_certificates', JSON.stringify(updatedLangs));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setEditingSection(null);
      setEditingLanguageIndex(null);
    } catch (error) {
      console.error('Error saving language:', error);
      throw error;
    }
  };

  // Handle saving employment updates
  const handleEmploymentSave = async (employmentEntry) => {
    try {
      const currentEmployment = dashboardData?.employment_history || [];
      let updatedEmployment;

      if (editingEmploymentIndex !== null) {
        updatedEmployment = [...currentEmployment];
        updatedEmployment[editingEmploymentIndex] = employmentEntry;
      } else {
        updatedEmployment = [...currentEmployment, employmentEntry];
      }

      const formData = new FormData();
      formData.append('employment_history', JSON.stringify(updatedEmployment));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowEmploymentModal(false);
      setEditingEmploymentIndex(null);
    } catch (error) {
      console.error('Error saving employment:', error);
      throw error;
    }
  };

  // Handle saving education updates
  const handleEducationSave = async (educationEntry) => {
    try {
      // Map frontend camelCase to snake_case for backend consistency
      const mappedEntry = {
        course_title: educationEntry.courseTitle || educationEntry.course_title,
        institution_name: educationEntry.institutionName || educationEntry.institution_name,
        course_level: educationEntry.courseLevel || educationEntry.course_level,
        start_date: educationEntry.startDate || educationEntry.start_date,
        end_date: educationEntry.endDate || educationEntry.end_date,
        description: educationEntry.description,
        grade: educationEntry.grade,
        passing_date: educationEntry.passingDate || educationEntry.passing_date,
        roll_no: educationEntry.rollNo || educationEntry.roll_no
      };

      const currentEducation = dashboardData?.education_history || [];
      let updatedEducation;

      if (editingEducationIndex !== null) {
        updatedEducation = [...currentEducation];
        updatedEducation[editingEducationIndex] = mappedEntry;
      } else {
        updatedEducation = [...currentEducation, mappedEntry];
      }

      const formData = new FormData();
      formData.append('education_history', JSON.stringify(updatedEducation));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowEducationModal(false);
      setEditingEducationIndex(null);
    } catch (error) {
      console.error('Error saving education:', error);
      throw error;
    }
  };

  // Handle saving certificate updates
  const handleCertificateSave = async (certificateEntry) => {
    try {
      const currentCerts = dashboardData?.certificates || [];
      let updatedCerts;

      if (editingCertificateIndex !== null) {
        updatedCerts = [...currentCerts];
        updatedCerts[editingCertificateIndex] = certificateEntry;
      } else {
        updatedCerts = [...currentCerts, certificateEntry];
      }

      const formData = new FormData();
      formData.append('certificates', JSON.stringify(updatedCerts));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowCertificateModal(false);
      setEditingCertificateIndex(null);
    } catch (error) {
      console.error('Error saving certificate:', error);
      throw error;
    }
  };

  // Handle saving document updates
  const handleDocumentSave = async (documentEntry) => {
    try {
      const currentDocs = dashboardData?.documents || [];
      let updatedDocs;

      if (editingDocumentIndex !== null) {
        updatedDocs = [...currentDocs];
        updatedDocs[editingDocumentIndex] = documentEntry;
      } else {
        updatedDocs = [...currentDocs, documentEntry];
      }

      const formData = new FormData();

      // Strip file objects before serializing - files are sent separately
      const cleanDocs = updatedDocs.map(d => {
        const { file, ...rest } = d;
        return rest;
      });
      formData.append('documents', JSON.stringify(cleanDocs));

      // Handle file upload if present
      if (documentEntry.file) {
        formData.append('document_file', documentEntry.file);
        // If editing, send the index to the backend so it knows which document to update
        if (editingDocumentIndex !== null) {
          formData.append('document_index', editingDocumentIndex);
        }
      }

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowDocumentModal(false);
      setEditingDocumentIndex(null);
    } catch (error) {
      console.error('Error saving document:', error);
      throw error;
    }
  };

  const handleDeleteDocument = async (index) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      const currentDocs = dashboardData?.documents || [];
      const updatedDocs = currentDocs.filter((_, i) => i !== index);

      const formData = new FormData();
      formData.append('documents', JSON.stringify(updatedDocs));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Failed to delete document');
    }
  };

  // Handle saving note updates
  const handleNoteSave = async (noteEntry) => {
    try {
      const currentNotes = dashboardData?.notes || [];
      let updatedNotes;

      if (editingNoteIndex !== null) {
        updatedNotes = [...currentNotes];
        updatedNotes[editingNoteIndex] = noteEntry;
      } else {
        updatedNotes = [...currentNotes, noteEntry];
      }

      const formData = new FormData();
      formData.append('notes', JSON.stringify(updatedNotes));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowNoteModal(false);
      setEditingNoteIndex(null);
    } catch (error) {
      console.error('Error saving note:', error);
      throw error;
    }
  };

  const handleSkillSave = async (newSkill) => {
    try {
      const currentSkills = dashboardData?.user?.skills || [];
      // Handle potential string vs array issues if needed, but assuming dashboardData.user.skills is an array
      const skillsArray = Array.isArray(currentSkills) ? currentSkills : [];

      // Prevent duplicates
      const skillName = typeof newSkill === 'object' ? newSkill.name : newSkill;
      const exists = skillsArray.some(s => (typeof s === 'object' ? s.name : s) === skillName);

      if (exists) {
        showToast('Skill already exists');
        return;
      }
      const updatedSkills = [...skillsArray, newSkill];

      const formData = new FormData();
      formData.append('skills', JSON.stringify(updatedSkills));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
      setShowSkillModal(false);
    } catch (error) {
      console.error('Error saving skill:', error);
      throw error;
    }
  };

  const handleDeleteSkill = async (skillToDelete) => {
    const skillName = typeof skillToDelete === 'object' ? skillToDelete.name : skillToDelete;
    if (!window.confirm(`Are you sure you want to delete "${skillName}"?`)) return;
    try {
      const currentSkills = dashboardData?.user?.skills || [];
      const skillsArray = Array.isArray(currentSkills) ? currentSkills : [];
      const updatedSkills = skillsArray.filter(s => {
        const name = typeof s === 'object' ? s.name : s;
        return name !== skillName;
      });

      const formData = new FormData();
      formData.append('skills', JSON.stringify(updatedSkills));

      await api.put('/student/profile', formData, {
        headers: { 'Content-Type': undefined }
      });

      await handleProfileSaved();
    } catch (error) {
      console.error('Error deleting skill:', error);
      showToast('Failed to delete skill');
    }
  };


  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/auth/reset-password', {
        password: newPassword,
        token: localStorage.getItem('token')
      });
      showToast('Password reset successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('[StudentDashboard] Error resetting password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center animate-fadeIn">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const user = dashboardData?.user;
  const { score: profileScore, missingFields } = calculateProfileCompletion(user);
  const skills = user?.skills || [];
  const certificates = user?.certificates || [];

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans text-sm relative">

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-200 shadow-xl flex-shrink-0 flex flex-col h-full transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-slate-100 bg-white/50 backdrop-blur-sm flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-lg shadow-cyan-500/10 border border-slate-200">
                <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-emerald-500 rounded-full border border-white"></div>
            </div>
            <div>
              <h1 className="text-sm font-bold text-cyan-700 tracking-tight">
                Polyglots
              </h1>
              <p className="text-[11px] text-slate-500 font-medium whitespace-nowrap">Candidate Dashboard</p>
            </div>
          </div>
          <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden text-slate-400 hover:text-cyan-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto custom-scrollbar">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 px-2">
            {t('dashboard.main') || 'Main'}
          </p>
          <SidebarItem
            icon={LayoutDashboard}
            label="Overview"
            active={activeTab === 'overview'}
            onClick={() => { navigate('/student/dashboard'); setIsMobileMenuOpen(false); }}
          />
          <SidebarItem
            icon={User}
            label="My Profile"
            active={activeTab === 'profile'}
            onClick={() => { navigate('/student/profile'); setIsMobileMenuOpen(false); }}
            badge={profileScore < 100 ? 'Incomplete' : null}
          />
          <SidebarItem
            icon={FileText}
            label="Applications"
            active={activeTab === 'interviews' || activeTab === 'applications'}
            onClick={() => { navigate('/student/interviews'); setIsMobileMenuOpen(false); }}
            badge={(interviews.length + jobApplications.length) > 0 ? (interviews.length + jobApplications.length) : null}
          />
          <SidebarItem
            icon={BookOpen}
            label="Language Classes"
            active={activeTab === 'language-classes'}
            onClick={() => { navigate('/student/language-classes'); setIsMobileMenuOpen(false); }}
          />

          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4 px-2">
            {t('dashboard.system') || 'System'}
          </p>
          <SidebarItem
            icon={Settings}
            label="Settings"
            active={activeTab === 'settings'}
            onClick={() => { navigate('/student/settings'); setIsMobileMenuOpen(false); }}
          />
        </div>

        <div className="p-3 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-slate-600 hover:text-red-500 hover:bg-red-50 transition-all group text-xs"
          >
            <LogOut className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative bg-slate-50">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-cyan-50/50 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-50/30 rounded-full blur-3xl pointer-events-none translate-x-1/2 -translate-y-1/2" />
        {/* Slightly Larger Sticky Header */}
        <header className="sticky top-0 z-20 w-full bg-white/80 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
          <div className="px-4 sm:px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 border-b-0 pb-0 mb-0">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden p-2 -ml-2 text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-800">
                  {activeTab === 'overview' ? 'Overview' :
                    activeTab === 'profile' ? 'My Profile' :
                      activeTab === 'interviews' ? 'Interviews' :
                        activeTab === 'language-classes' ? 'Language Classes' :
                          'Settings'}
                </h2>
                {activeTab !== 'overview' && (
                  <p className="text-xs sm:text-sm text-slate-500 truncate max-w-[150px] sm:max-w-none">
                    Welcome back, {user?.name?.split(' ')[0] || 'Student'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <NotificationBell
                userRole="candidate"
              />
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-2.5">
                {user?.photo_url ? (
                  <img
                    src={getPhotoUrl(user.photo_url)}
                    alt={user.name}
                    className="h-8 w-8 rounded-full object-cover border border-slate-200 shadow-sm"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 text-slate-500">
                    <User className="h-4 w-4" />
                  </div>
                )}
                <div className="hidden md:block text-right">
                  <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-[11px] text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </header >

        {/* Scrollable Content */}
        < main className="flex-1 overflow-y-auto p-3 scroll-smooth" >
          <div className="max-w-6xl mx-auto space-y-3 pb-6">
            {/* Email Verification Warning */}
            {user && !user.email_verified && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-center gap-2 text-amber-800">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                <div className="text-xs">
                  <span className="font-bold">Please verify your email address.</span>
                  <span className="ml-1 text-amber-700">Check your inbox for the verification link.</span>
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="animate-fadeIn space-y-6">
                {/* Header Section */}
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 font-outfit">Welcome back, {user?.name?.split(' ')[0] || 'Alex'}</h1>
                    <p className="text-slate-500 mt-1">Here's what's happening with your profile today.</p>
                  </div>
                </div>


                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Profile Header Card - 2 Columns */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      {/* Banner */}
                      <div className="h-32 bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-200 relative">
                        <div className="absolute -bottom-12 left-8 p-1 bg-white rounded-full shadow-lg">
                          {user?.photo_url ? (
                            <img
                              src={getPhotoUrl(user.photo_url)}
                              alt={user.name}
                              className="h-24 w-24 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-24 w-24 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 font-bold text-2xl">
                              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AM'}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Profile Info */}
                      <div className="pt-16 pb-6 px-4 md:px-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-bold text-slate-900">{user?.name}</h2>
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-bold ring-1 ring-emerald-500/20">
                              <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse"></span>
                              Open to work
                            </span>
                          </div>
                          <p className="text-slate-500 flex items-center gap-2 mt-1">
                            <Briefcase className="h-4 w-4" />
                            {user?.occupation || (dashboardData?.employment_history?.[0]?.job_title) || 'Professional'} @ {(dashboardData?.employment_history?.[0]?.employer) || 'Talent Portal'}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setProfileSubTab('profile');
                            navigate('/student/profile');
                            setIsEditingProfile(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all font-medium text-sm text-slate-700 shadow-sm"
                        >
                          <Edit2 className="h-4 w-4" />
                          Edit Profile
                        </button>
                      </div>

                      {/* Stats Bar */}
                      <div className="px-4 md:px-8 pb-8 pt-4 border-t border-slate-50 grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Experience</p>
                          <p className="text-lg font-bold text-slate-900">
                            {(() => {
                              const history = dashboardData?.employment_history || [];
                              if (history.length === 0) return '0 Years';
                              let totalMonths = 0;
                              history.forEach(exp => {
                                if (exp.start_date) {
                                  const start = new Date(exp.start_date);
                                  const end = exp.end_date ? new Date(exp.end_date) : new Date();
                                  totalMonths += (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                                }
                              });
                              const years = Math.floor(totalMonths / 12);
                              return years > 0 ? `${years} Years` : 'Fresh';
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Location</p>
                          <p className="text-lg font-bold text-slate-900">{user?.city || user?.country || 'Not Set'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Status</p>
                          <p className="text-lg font-bold text-slate-900 capitalize">{user?.approval_status || 'Pending'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Member Since</p>
                          <p className="text-lg font-bold text-slate-900">{user?.created_at ? new Date(user.created_at).getFullYear() : new Date().getFullYear()}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Top Skills Card */}
                      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold text-slate-900">Top Skills</h3>
                          <button
                            onClick={() => {
                              setProfileSubTab('skills');
                              setShowSkillModal(true);
                            }}
                            className="p-1.5 text-slate-400 hover:text-cyan-500 transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-6">Based on your resume</p>

                        <div className="flex flex-wrap gap-2 flex-1 content-start">
                          {(() => {
                            const skills = Array.isArray(dashboardData?.user?.skills) ? dashboardData.user.skills : [];
                            if (skills.length === 0) return (
                              <div className="w-full py-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                <p className="text-xs text-slate-400 text-center w-full">No skills added</p>
                              </div>
                            );
                            return (
                              <>
                                {skills.slice(0, 6).map((skill, idx) => (
                                  <span key={idx} className="px-3 py-1.5 bg-slate-50 text-slate-700 rounded-full text-xs font-semibold border border-slate-100">
                                    {typeof skill === 'object' ? skill.name : skill}
                                  </span>
                                ))}
                                {skills.length > 6 && (
                                  <span className="px-3 py-1.5 bg-cyan-50 text-cyan-600 rounded-full text-xs font-bold ring-1 ring-cyan-500/10">
                                    +{skills.length - 6} More
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        <button
                          onClick={() => { setProfileSubTab('skills'); navigate('/student/profile'); }}
                          className="w-full mt-6 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all"
                        >
                          Manage Skills
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Sidebar Widgets - 1 Column */}
                  <div className="space-y-6">
                    {/* Profile Strength Widget */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 overflow-hidden">
                      <div className="flex items-center justify-between mb-8">
                        <h3 className="text-lg font-bold text-slate-900">Profile Strength</h3>
                        <div className="relative h-16 w-16">
                          {/* Circular Progress (Simple approach using CSS vars or SVG) */}
                          <svg className="h-full w-full transform -rotate-90">
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              className="stroke-slate-100"
                              strokeWidth="4"
                              fill="transparent"
                            />
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              className="stroke-indigo-500 transition-all duration-1000"
                              strokeWidth="4"
                              fill="transparent"
                              strokeDasharray={2 * Math.PI * 28}
                              strokeDashoffset={2 * Math.PI * 28 * (1 - (profileScore / 100))}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-600">{profileScore}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <p className="text-xs text-slate-500 font-medium">Complete these steps to reach 100%:</p>
                        <div className="space-y-3">
                          {missingFields?.length > 0 ? (
                            missingFields.slice(0, 3).map((field, idx) => (
                              <div key={idx} className="flex items-center gap-2 group cursor-pointer" onClick={() => navigate('/student/profile')}>
                                <div className="h-5 w-5 rounded-full border-2 border-slate-200 flex items-center justify-center shrink-0 group-hover:border-cyan-400 transition-colors">
                                  <div className="h-2 w-2 rounded-full bg-slate-200 group-hover:bg-cyan-400 transition-all"></div>
                                </div>
                                <span className="text-[13px] text-slate-600 group-hover:text-slate-900 truncate">Add {field}</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center gap-2 text-emerald-600 font-bold text-[13px]">
                              <CheckCircle className="h-5 w-5" />
                              All done! You are at 100%
                            </div>
                          )}
                        </div>

                        {profileScore < 100 && (
                          <div className="mt-6 p-4 bg-indigo-50 rounded-2xl border border-indigo-100 relative overflow-hidden group">
                            <div className="relative z-10">
                              <h4 className="text-[13px] font-bold text-indigo-900 mb-1">Add details</h4>
                              <p className="text-[11px] text-indigo-700 mb-3 opacity-80">Better profiles get 35% more views.</p>
                              <button
                                onClick={() => navigate('/student/profile')}
                                className="py-1.5 px-4 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-600/20"
                              >
                                Update Profile
                              </button>
                            </div>
                            <div className="absolute top-0 right-0 h-16 w-16 bg-white/10 rounded-full transform translate-x-4 -translate-y-4" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Important Updates Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none">
                        <Bell className="h-24 w-24 text-indigo-500 rotate-12" />
                      </div>
                      <div className="flex items-center gap-3 mb-6 relative z-10">
                        <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center shadow-sm">
                          <Bell className="h-5 w-5 text-indigo-500" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-cyan-600">
                            {(() => {
                              const val = t('studentDashboard.importantUpdates');
                              return val === 'studentDashboard.importantUpdates' ? 'Important Updates' : val;
                            })()}
                          </h3>
                          <p className="text-[11px] text-slate-400 font-medium">STAY INFORMED ON YOUR PROGRESS</p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        {/* Custom Administrative Updates */}
                        {notifications.filter(n => n.type === 'important_update').map((n, idx) => (
                          <div key={idx} className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-cyan-600 mt-2 shrink-0 animate-pulse" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-cyan-700 transition-colors">
                                {(() => {
                                  const val = t('studentDashboard.importantUpdate');
                                  return val === 'studentDashboard.importantUpdate' ? 'Important Update:' : val;
                                })()}
                              </h4>
                              <p className="text-xs text-slate-500 leading-relaxed">{n.message}</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">
                                {(() => {
                                  const val = t('studentDashboard.moderatorUpdate');
                                  return val === 'studentDashboard.moderatorUpdate' ? 'MODERATOR UPDATE' : val;
                                })()} • {new Date(n.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}

                        {/* Automated: Email Verification */}
                        {user?.email_verified ? (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">Email Verified</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Your email address has been successfully verified.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">SYSTEM NOTIFICATION</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-amber-500 mt-2 shrink-0 animate-pulse" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors">Email Not Verified</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Please check your inbox and verify your email address to enable all features.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">ACTION REQUIRED</p>
                            </div>
                          </div>
                        )}

                        {/* Automated: Approval Status */}
                        {user?.approval_status === 'approved' ? (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">Profile Approved</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Your profile has been reviewed and approved by our team.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">SYSTEM NOTIFICATION</p>
                            </div>
                          </div>
                        ) : user?.approval_status === 'rejected' ? (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-red-500 mt-2 shrink-0" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-red-600 transition-colors">Action Required</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Your profile requires updates. Please check your emails or notes for feedback.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">SYSTEM NOTIFICATION</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-amber-500 mt-2 shrink-0 animate-pulse" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors">Verification Pending</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Our team is currently reviewing your profile. We'll notify you once it's approved.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">IN PROGRESS</p>
                            </div>
                          </div>
                        )}

                        {/* Automated: Profile Completion */}
                        {profileScore < 100 ? (
                          <div className="flex gap-4 opacity-70 hover:opacity-100 transition-opacity">
                            <div className="h-2 w-2 rounded-full bg-cyan-300 mt-2 shrink-0" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-800 mb-1">Complete Your Profile</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">A complete profile significantly improves your visibility to employers. ({profileScore}% complete)</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">REACTION REQUIRED</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-4 group">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
                            <div>
                              <h4 className="text-sm font-bold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">Profile Completed</h4>
                              <p className="text-xs text-slate-500 leading-relaxed">Excellent! Your profile is 100% complete.</p>
                              <p className="text-[10px] text-slate-400 mt-2 font-medium tracking-wide">ACHIEVEMENT</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Primary Progress Section (Moved down) */}
                {dashboardData?.user && (
                  <div className="mb-6 mt-8">
                    <h3 className="text-xl font-bold text-slate-800 mb-4">Overall Progress</h3>
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                      {jobApplications.length > 0 ? (
                        /* Show progress from the most advanced job application */
                        (() => {
                          const primary = jobApplications.reduce((best, app) => {
                            const bestIdx = PIPELINE_STAGES.indexOf(best.stage);
                            const appIdx = PIPELINE_STAGES.indexOf(app.stage);
                            return appIdx > bestIdx ? app : best;
                          }, jobApplications[0]);
                          return (
                            <>
                              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
                                <div>
                                  <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Current Stage</p>
                                  <p className="text-2xl font-black text-cyan-600">{primary.stage}</p>
                                  <p className="text-sm text-slate-600 mt-2 font-medium bg-slate-50 px-3 py-1.5 rounded-lg inline-flex items-center border border-slate-100">
                                    <span className="text-slate-400 mr-2">Job:</span> {primary.job_title}
                                    {primary.company_name && <span className="ml-2 text-slate-400">• {primary.company_name}</span>}
                                  </p>
                                </div>
                                <button
                                  onClick={() => setShowProgressModal(true)}
                                  className="px-5 py-2.5 bg-cyan-50 hover:bg-cyan-600 text-cyan-600 hover:text-white rounded-xl font-bold transition-all shadow-sm border border-cyan-100 hover:border-transparent"
                                >
                                  View Details
                                </button>
                              </div>
                              <ApplicationStatusStepper currentStage={primary.stage} />
                            </>
                          );
                        })()
                      ) : (
                        /* Fallback: No jobs assigned yet */
                        <div className="flex flex-col items-center justify-center p-8 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                          <Check className="h-10 w-10 mb-3 opacity-50 text-slate-300" />
                          <p className="text-sm font-bold text-slate-600">Pending Job Assignment</p>
                          <p className="text-xs mt-1 text-center max-w-sm">
                            Your profile is active. Once Staff assigns you to a job posting, your pipeline progress will tracked here.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Job Applications Summary on Overview */}
                {jobApplications.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-4">My Job Applications ({jobApplications.length})</h3>
                    <div className="grid gap-3">
                      {jobApplications.map(app => {
                        const colors = STAGE_COLORS[app.stage] || { light: 'bg-gray-50', text: 'text-gray-700' };
                        return (
                          <div key={app.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow cursor-pointer"
                            onClick={() => navigate('/student/applications')}>
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-bold text-slate-900">{app.job_title}</h4>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {app.company_name}{app.job_location && ` • ${app.job_location}`}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${colors.light} ${colors.text}`}>
                                {app.stage}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'profile' && (
              <div className="space-y-6 animate-fadeIn">
                {/* Profile Header Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="relative group">
                      {user?.photo_url ? (
                        <img
                          src={getPhotoUrl(user.photo_url)}
                          alt={user.name}
                          className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
                        />
                      ) : (
                        <div className="h-24 w-24 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center border-4 border-white shadow-lg">
                          <User className="h-12 w-12 text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 right-0 bg-cyan-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-white">
                        {profileScore}%
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-2 text-sm text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span>{user?.email}</span>
                        </div>
                        {user?.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span>{user?.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => generateProfilePDF(user, dashboardData, false, 'en')}
                        className="px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-700 text-white rounded-lg hover:from-blue-800 hover:to-indigo-800 transition-all text-sm font-medium flex items-center gap-2 shadow-sm"
                      >
                        <Download className="h-4 w-4" />
                        🇪🇺 EU Resume (EN)
                      </button>
                      <button
                        onClick={() => generateProfilePDF(user, dashboardData, false, 'de')}
                        className="px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-lg hover:from-slate-800 hover:to-slate-900 transition-all text-sm font-medium flex items-center gap-2 shadow-sm"
                      >
                        <Download className="h-4 w-4" />
                        🇩🇪 EU Lebenslauf (DE)
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabs for Profile sections */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                  <div className="border-b border-slate-200 flex-shrink-0">
                    <div className="flex overflow-x-auto no-scrollbar whitespace-nowrap px-4 sm:px-6 gap-6">
                      <button
                        onClick={() => setProfileSubTab('profile')}
                        className={`py-4 text-sm font-medium transition-all ${profileSubTab === 'profile' ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        Profile
                      </button>
                      <button
                        onClick={() => setProfileSubTab('employment')}
                        className={`py-4 text-sm font-medium transition-all ${profileSubTab === 'employment' ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        Employment & Qualification
                      </button>
                      <button
                        onClick={() => setProfileSubTab('skills')}
                        className={`py-4 text-sm font-medium transition-all ${profileSubTab === 'skills' ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        Skills & Certificates
                      </button>
                      <button
                        onClick={() => setProfileSubTab('documents')}
                        className={`py-4 text-sm font-medium transition-all ${profileSubTab === 'documents' ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        Documents
                      </button>
                      <button
                        onClick={() => setProfileSubTab('notes')}
                        className={`py-4 text-sm font-medium transition-all ${profileSubTab === 'notes' ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        Notes
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Profile Tab Content */}
                    {profileSubTab === 'profile' && (
                      <>
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Personal Details</h3>
                            <button
                              onClick={() => setEditingSection('personal')}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="flex items-start gap-2">
                              <User className="h-4 w-4 text-slate-400 mt-0.5" />
                              <div>
                                <p className="text-xs text-slate-500">Gender</p>
                                <p className="text-sm font-medium text-slate-800">{user?.gender || '-'}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Calendar className="h-4 w-4 text-slate-400 mt-0.5" />
                              <div>
                                <p className="text-xs text-slate-500">Date Of Birth</p>
                                <p className="text-sm font-medium text-slate-800">
                                  {user?.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                              <div>
                                <p className="text-xs text-slate-500">Candidate Ref. No</p>
                                <p className="text-sm font-medium text-slate-800">{user?.candidate_ref_number || '-'}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Globe className="h-4 w-4 text-slate-400 mt-0.5" />
                              <div>
                                <p className="text-xs text-slate-500">Native Language</p>
                                <p className="text-sm font-medium text-slate-800">{user?.native_language || '-'}</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed">
                            {user?.about_me || user?.bio || 'No bio added yet.'}
                          </p>
                        </div>

                        {/* Address Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-bold text-slate-900">Address</h3>
                            <button
                              onClick={() => setEditingSection('address')}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                            <p className="text-sm text-slate-600">
                              {[user?.house_number, user?.street_name, user?.city, user?.state, user?.country, user?.postal_code]
                                .filter(Boolean).join(', ') || user?.address || 'No address added yet.'}
                            </p>
                          </div>
                        </div>

                        {/* Nationality Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-bold text-slate-900">Nationality</h3>
                            <button
                              onClick={() => setEditingSection('nationality')}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-slate-800">{user?.nationality || 'India'}</span>
                            {user?.identification_number && (
                              <span className="text-sm text-slate-600">{user.identification_number}</span>
                            )}
                          </div>
                        </div>

                        {/* Language Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Language</h3>
                            <button
                              onClick={() => {
                                setEditingLanguageIndex(null);
                                setEditingSection('language');
                              }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {(() => {
                            const source = dashboardData?.language_certificates || dashboardData?.language_certs || [];
                            const langs = Array.isArray(source) ? source : [];

                            if (langs.length === 0) {
                              return <p className="text-sm text-slate-400 text-center py-4">No language certificates added yet. Click + to add one.</p>;
                            }

                            return (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                      <th className="py-2 font-medium">Language</th>
                                      <th className="py-2 font-medium">Listening</th>
                                      <th className="py-2 font-medium">Reading</th>
                                      <th className="py-2 font-medium">Speaking</th>
                                      <th className="py-2 font-medium">Writing</th>
                                      <th className="py-2 font-medium w-10"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {langs.map((lang, i) => (
                                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                        <td className="py-3 font-medium text-slate-800">{lang.language}</td>
                                        <td className="py-3 text-cyan-600 font-semibold">{lang.listening || lang.listening_level || lang.level || '-'}</td>
                                        <td className="py-3 text-cyan-600 font-semibold">{lang.reading || lang.reading_level || lang.level || '-'}</td>
                                        <td className="py-3 text-cyan-600 font-semibold">{lang.speaking || lang.speaking_level || lang.level || '-'}</td>
                                        <td className="py-3 text-cyan-600 font-semibold">{lang.writing || lang.writing_level || lang.level || '-'}</td>
                                        <td className="py-3">
                                          <button
                                            onClick={() => {
                                              setEditingLanguageIndex(i);
                                              setEditingSection('language');
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                          >
                                            <Edit2 className="h-3.5 w-3.5" />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </div>
                      </>
                    )}

                    {/* Employment & Qualification Tab Content */}
                    {profileSubTab === 'employment' && (
                      <>
                        {/* Employment Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Employment</h3>
                            <button
                              onClick={() => {
                                setEditingEmploymentIndex(null);
                                setShowEmploymentModal(true);
                              }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {(() => {
                            const employment = dashboardData?.employment_history || [];
                            const jobs = Array.isArray(employment) ? employment : [];

                            return jobs.length > 0 ? (
                              <div className="space-y-3">
                                {jobs.map((job, i) => (
                                  <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 bg-cyan-50 rounded-lg">
                                        <Briefcase className="h-5 w-5 text-cyan-600" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-slate-800">{job.title}</p>
                                        <p className="text-sm text-slate-600">{job.company_name || job.company}</p>
                                        <p className="text-xs text-slate-400 mt-1">
                                          {job.start_date} - {job.no_end_date || job.is_current ? 'Present' : job.end_date}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingEmploymentIndex(i);
                                        setShowEmploymentModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 text-center py-4">No employment history added yet. Click + to add one.</p>
                            );
                          })()}
                        </div>

                        {/* Education Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Education</h3>
                            <button
                              onClick={() => {
                                setEditingEducationIndex(null);
                                setShowEducationModal(true);
                              }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {(() => {
                            const education = dashboardData?.education_history || [];
                            const edus = Array.isArray(education) ? education : [];

                            return edus.length > 0 ? (
                              <div className="space-y-3">
                                {edus.map((edu, i) => (
                                  <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 bg-purple-50 rounded-lg">
                                        <GraduationCap className="h-5 w-5 text-purple-600" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-slate-800">{edu.course_title || edu.qualification || edu.degree}</p>
                                        <p className="text-sm text-slate-600">{edu.institution_name || edu.university || edu.institution}</p>
                                        <p className="text-xs text-slate-400 mt-1">
                                          {edu.course_level || edu.degree_type || edu.field_of_study} • {edu.start_date} - {edu.end_date || edu.year_of_passout}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingEducationIndex(i);
                                        setShowEducationModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 text-center py-4">No education history added yet. Click + to add one.</p>
                            );
                          })()}
                        </div>
                      </>
                    )}

                    {/* Skills & Certificates Tab Content */}
                    {profileSubTab === 'skills' && (
                      <div className="space-y-6">
                        {/* Skills Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Professional Skills</h3>
                            <button
                              onClick={() => setShowSkillModal(true)}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>

                          {skills && skills.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {skills.map((skill, index) => (
                                <div key={index} className="group relative px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 font-medium text-sm flex items-center gap-2 hover:border-cyan-300 hover:shadow-sm transition-all">
                                  <div className="flex flex-col">
                                    <span className="font-bold">{typeof skill === 'object' ? skill.name : skill}</span>
                                    {typeof skill === 'object' && (
                                      <div className="flex items-center gap-1.5 mt-0.5">
                                        <span className="text-[10px] text-cyan-600 font-black uppercase tracking-widest">{skill.proficiency}</span>
                                        {skill.years_of_experience && (
                                          <>
                                            <span className="text-slate-300">•</span>
                                            <span className="text-[10px] text-slate-400 font-bold">{skill.years_of_experience} Years Exp.</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleDeleteSkill(skill)}
                                    className="ml-2 p-1 text-slate-300 hover:text-red-500 rounded-full hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                    title="Remove skill"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 text-center py-4">No skills added yet. Click + to add one.</p>
                          )}
                        </div>

                        {/* Certificates Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-slate-900">Licenses & Certificates</h3>
                            <button
                              onClick={() => {
                                setEditingCertificateIndex(null);
                                setShowCertificateModal(true);
                              }}
                              className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {(() => {
                            const certs = dashboardData?.certificates || [];
                            const certList = Array.isArray(certs) ? certs : [];

                            return certList.length > 0 ? (
                              <div className="space-y-3">
                                {certList.map((cert, i) => (
                                  <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 bg-amber-50 rounded-lg">
                                        <Award className="h-5 w-5 text-amber-600" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-slate-800">{cert.license_title || cert.title || cert.name}</p>
                                        <p className="text-sm text-slate-600">Issued by: {cert.issued_by || cert.issuing_organization}</p>
                                        <p className="text-xs text-slate-400 mt-1">
                                          {cert.issuance_date || cert.issue_date} - {cert.no_end_date || cert.no_expiry ? 'No Expiry' : cert.expiry_date || cert.expiration_date}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingCertificateIndex(i);
                                        setShowCertificateModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 text-center py-4">No licenses or certificates added yet. Click + to add one.</p>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Documents Tab Content */}
                    {profileSubTab === 'documents' && (
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base font-bold text-slate-900">Documents</h3>
                          <button
                            onClick={() => {
                              setEditingDocumentIndex(null);
                              setShowDocumentModal(true);
                            }}
                            className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        {(() => {
                          const docs = dashboardData?.documents || [];
                          const docList = Array.isArray(docs) ? docs : [];

                          return docList.length > 0 ? (
                            <div className="space-y-3">
                              {docList.map((doc, i) => (
                                <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-red-50 rounded-lg">
                                      <FileText className="h-5 w-5 text-red-500" />
                                    </div>
                                    <div>
                                      <p className="font-semibold text-slate-800">{doc.file_name || doc.name}</p>
                                      <p className="text-xs text-slate-400">
                                        Uploaded on {doc.uploaded_on || doc.created_at} • {doc.document_type || doc.type}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        setEditingDocumentIndex(i);
                                        setShowDocumentModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                      title="Edit document"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDocument(i)}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                      title="Delete document"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                    {(doc.file_url || doc.url) && (
                                      <>
                                        <button
                                          onClick={() => handleViewDocument(doc.file_url || doc.url)}
                                          className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-all"
                                          title="View document"
                                        >
                                          <Eye className="h-4 w-4" />
                                        </button>
                                        <a
                                          href={getPhotoUrl(doc.file_url || doc.url)}
                                          download={doc.original_filename || doc.file_name || doc.name || 'document'}
                                          className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                          title="Download document"
                                        >
                                          <Download className="h-4 w-4" />
                                        </a>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 text-center py-4">No documents uploaded yet. Click + to add one.</p>
                          );
                        })()}
                      </div>
                    )}

                    {/* Notes Tab Content */}
                    {profileSubTab === 'notes' && (
                      <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base font-bold text-slate-900">Notes</h3>
                          <button
                            onClick={() => {
                              setEditingNoteIndex(null);
                              setShowNoteModal(true);
                            }}
                            className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all text-sm font-medium flex items-center gap-2"
                          >
                            <Plus className="h-4 w-4" />
                            Add Note
                          </button>
                        </div>
                        {(() => {
                          const notes = dashboardData?.notes || [];
                          const noteList = Array.isArray(notes) ? notes : [];

                          return noteList.length > 0 ? (
                            <div className="space-y-3">
                              {noteList.map((note, i) => (
                                <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="text-slate-700 whitespace-pre-wrap">{note.content || note.text || note.note}</p>
                                      <p className="text-xs text-slate-400 mt-2">
                                        {note.created_at ? new Date(note.created_at).toLocaleDateString() : ''}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingNoteIndex(i);
                                        setShowNoteModal(true);
                                      }}
                                      className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all ml-2"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 text-center py-4">No notes added yet. Click Add Note to create one.</p>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Edit Modals */}
                <EditPersonalDetailsModal
                  isOpen={editingSection === 'personal'}
                  onClose={() => setEditingSection(null)}
                  userData={user}
                  onSave={handleSectionSave}
                  getPhotoUrl={getPhotoUrl}
                />
                <EditAddressModal
                  isOpen={editingSection === 'address'}
                  onClose={() => setEditingSection(null)}
                  userData={user}
                  onSave={handleSectionSave}
                />
                <EditNationalityModal
                  isOpen={editingSection === 'nationality'}
                  onClose={() => setEditingSection(null)}
                  userData={user}
                  onSave={handleSectionSave}
                />
                <EditLanguageModal
                  isOpen={editingSection === 'language'}
                  onClose={() => {
                    setEditingSection(null);
                    setEditingLanguageIndex(null);
                  }}
                  languageData={editingLanguageIndex !== null ? (dashboardData?.language_certificates || [])[editingLanguageIndex] : null}
                  onSave={handleLanguageSave}
                  isAddMode={editingLanguageIndex === null}
                />
                <AddEmploymentModal
                  isOpen={showEmploymentModal}
                  onClose={() => {
                    setShowEmploymentModal(false);
                    setEditingEmploymentIndex(null);
                  }}
                  employmentData={editingEmploymentIndex !== null ? (dashboardData?.employment_history || [])[editingEmploymentIndex] : null}
                  onSave={handleEmploymentSave}
                  isAddMode={editingEmploymentIndex === null}
                />
                <AddEducationModal
                  isOpen={showEducationModal}
                  onClose={() => {
                    setShowEducationModal(false);
                    setEditingEducationIndex(null);
                  }}
                  educationData={editingEducationIndex !== null ? (dashboardData?.education_history || [])[editingEducationIndex] : null}
                  onSave={handleEducationSave}
                  isAddMode={editingEducationIndex === null}
                />
                <AddCertificateModal
                  isOpen={showCertificateModal}
                  onClose={() => {
                    setShowCertificateModal(false);
                    setEditingCertificateIndex(null);
                  }}
                  certificateData={editingCertificateIndex !== null ? (dashboardData?.certificates || [])[editingCertificateIndex] : null}
                  onSave={handleCertificateSave}
                  isAddMode={editingCertificateIndex === null}
                />
                <AddDocumentModal
                  isOpen={showDocumentModal}
                  onClose={() => {
                    setShowDocumentModal(false);
                    setEditingDocumentIndex(null);
                  }}
                  documentData={editingDocumentIndex !== null ? (dashboardData?.documents || [])[editingDocumentIndex] : null}
                  onSave={handleDocumentSave}
                  isAddMode={editingDocumentIndex === null}
                />
                <AddNoteModal
                  isOpen={showNoteModal}
                  onClose={() => {
                    setShowNoteModal(false);
                    setEditingNoteIndex(null);
                  }}
                  noteData={editingNoteIndex !== null ? (dashboardData?.notes || [])[editingNoteIndex] : null}
                  onSave={handleNoteSave}
                  isAddMode={editingNoteIndex === null}
                />
              </div>
            )}

            {activeTab === 'interviews' && (
              <div className="animate-fadeIn p-4 sm:p-6 md:p-8 space-y-6">
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden min-h-[700px] flex flex-col">
                  {/* Tab Header */}
                  <div className="p-8 lg:p-12 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-8 bg-slate-50/30">
                    <div>
                      <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight leading-none mb-3">Interviews & Applications</h2>
                      <p className="text-slate-500 text-base leading-relaxed max-w-xl font-medium">
                        Track your career progress, manage applications, and join your scheduled interview sessions effortlessly.
                      </p>
                    </div>

                    {/* High-end Tab Switcher */}
                    <div className="flex bg-slate-200/50 p-1.5 rounded-[20px] w-fit shrink-0 border border-slate-200 shadow-inner">
                      <button
                        onClick={() => setInterviewsSubTab('applications')}
                        className={`flex items-center gap-3 px-7 py-3 rounded-[16px] text-sm font-bold transition-all duration-300 ${interviewsSubTab === 'applications'
                          ? 'bg-white text-cyan-600 shadow-xl shadow-cyan-900/10 active:scale-95'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                          }`}
                      >
                        <FileText className={`h-4.5 w-4.5 transition-transform duration-300 ${interviewsSubTab === 'applications' ? 'scale-110' : ''}`} />
                        All Job Applications
                      </button>
                      <button
                        onClick={() => setInterviewsSubTab('scheduled')}
                        className={`flex items-center gap-3 px-7 py-3 rounded-[16px] text-sm font-bold transition-all duration-300 ${interviewsSubTab === 'scheduled'
                          ? 'bg-white text-cyan-600 shadow-xl shadow-cyan-900/10 active:scale-95'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                          }`}
                      >
                        <Calendar className={`h-4.5 w-4.5 transition-transform duration-300 ${interviewsSubTab === 'scheduled' ? 'scale-110' : ''}`} />
                        Scheduled Interviews
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6 lg:p-12 flex-1">
                    {interviewsSubTab === 'applications' ? (
                      <div className="animate-fadeIn">
                        {jobApplications.length > 0 ? (
                          <div className="space-y-6">
                            {jobApplications.map(app => {
                              const stageIndex = PIPELINE_STAGES.indexOf(app.stage);
                              const colors = STAGE_COLORS[app.stage] || { bg: 'bg-gray-500', light: 'bg-gray-50', text: 'text-gray-700' };

                              return (
                                <div key={app.id} className="group p-6 md:p-8 bg-[#0f172a] border border-slate-800 rounded-2xl hover:border-cyan-500 transition-all shadow-2xl relative overflow-hidden">
                                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                  <div className="absolute top-0 left-0 w-1.5 h-full bg-cyan-500 rounded-l-2xl" />

                                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 z-10 relative">
                                    <div className="flex-1">
                                      <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                                        {app.job_title}
                                      </h3>
                                      <p className="text-cyan-400/90 font-semibold text-base mb-4 flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                                        {app.company_name || 'Global Employer'}
                                      </p>

                                      <div className="flex flex-wrap items-center gap-5 text-sm font-medium text-slate-400 mb-6">
                                        {app.job_location && (
                                          <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 text-slate-500" />
                                            <span>{app.job_location}</span>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                          <Calendar className="h-4 w-4 text-slate-500" />
                                          <span>Assigned: {new Date(app.assigned_at).toLocaleDateString()}</span>
                                        </div>
                                      </div>

                                      <div className="flex gap-1 mt-2">
                                        {PIPELINE_STAGES.map((stage, idx) => (
                                          <div key={stage}
                                            className={`h-2 flex-1 rounded-full transition-all duration-500 ${idx <= stageIndex ? colors.bg : 'bg-slate-800'
                                              }`}
                                            title={stage}
                                          />
                                        ))}
                                      </div>
                                    </div>

                                    <div className="flex flex-col items-end gap-3 shrink-0">
                                      <span className={`px-6 py-2 rounded-full text-sm font-extrabold border flex items-center gap-2 shadow-lg ${colors.light} ${colors.text} border-white/10`}>
                                        <Clock className="h-4 w-4" />
                                        {app.stage}
                                      </span>
                                      {app.required_german_level && (
                                        <span className="text-xs text-slate-500 font-bold bg-slate-800/50 px-3 py-1 rounded-lg border border-slate-700">German: {app.required_german_level}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {rejectedApplications.length > 0 && (
                              <div className="mt-12 pt-12 border-t border-slate-100">
                                <h3 className="text-xl font-bold text-red-500 mb-6 flex items-center gap-3">
                                  <XCircle className="h-6 w-6" /> Archived Applications
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {rejectedApplications.map(ra => (
                                    <div key={ra.id} className="p-5 bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h4 className="font-bold text-slate-800">{ra.job_title}</h4>
                                          <p className="text-sm text-slate-500">{ra.company_name}</p>
                                          {ra.rejection_reason && <p className="text-xs text-red-400 mt-2 font-medium">Reason: {ra.rejection_reason}</p>}
                                        </div>
                                        <span className="text-xs text-slate-400 font-medium">{new Date(ra.rejected_at).toLocaleDateString()}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-24 text-slate-400 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                            <div className="h-20 w-20 bg-white rounded-3xl shadow-sm flex items-center justify-center mb-6">
                              <FileText className="h-10 w-10 text-slate-300" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-700 mb-2">No active applications</h3>
                            <p className="text-slate-500 max-w-sm text-center">Once you are assigned to job postings by the Staff, your recruitment pipeline will appear here.</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="animate-fadeIn space-y-6">
                        {loading ? (
                          <div className="flex flex-col items-center justify-center py-20 bg-slate-50 rounded-3xl border border-slate-100">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600 mb-4"></div>
                            <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Syncing your schedule...</p>
                          </div>
                        ) : interviews.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-24 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                            <div className="h-20 w-20 bg-white rounded-3xl shadow-sm flex items-center justify-center mb-6">
                              <Calendar className="h-10 w-10 text-slate-300" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-700 mb-2">Your schedule is clear</h3>
                            <p className="text-slate-500 max-w-sm text-center">When recruiters schedule interviews with you, they will show up here with joining links.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-5">
                            {interviews.map((interview) => (
                              <div key={interview.id} className="flex flex-col md:flex-row items-stretch md:items-center bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-900/5 transition-all duration-300 gap-6">
                                {/* Date and Time Box */}
                                <div className="flex flex-row md:flex-col items-center justify-center min-w-[120px] bg-gradient-to-br from-cyan-600 to-indigo-700 rounded-2xl p-4 text-white shadow-lg shadow-cyan-600/20">
                                  <div className="flex flex-col items-center">
                                    <span className="text-2xl font-black leading-none">
                                      {interview.time?.substring(0, 5) || 'TBA'}
                                    </span>
                                    <span className="text-[10px] font-bold uppercase tracking-widest mt-1 opacity-80">
                                      {interview.time?.includes('PM') ? 'PM' : 'AM'}
                                    </span>
                                  </div>
                                  <div className="w-px h-8 bg-white/20 mx-4 md:hidden" />
                                  <div className="md:w-full md:h-px md:bg-white/20 md:my-3" />
                                  <span className="text-sm font-black uppercase tracking-tight">
                                    {new Date(interview.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>

                                {/* Interview Details */}
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <h3 className="text-xl font-extrabold text-slate-900">{interview.job_title || 'General Interview'}</h3>
                                    <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm ${interview.status === 'scheduled' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                      interview.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                        'bg-red-50 text-red-600 border-red-200'
                                      }`}>
                                      {interview.status}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-5">
                                    <p className="text-sm text-slate-500 flex items-center gap-2 font-medium">
                                      <span className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                        <User className="h-4 w-4" />
                                      </span>
                                      Recruiter: <span className="font-bold text-slate-900">{interview.interviewer_name}</span>
                                    </p>
                                    <div className="h-4 w-px bg-slate-200 hidden md:block" />
                                    <p className="text-sm text-slate-500 flex items-center gap-2 font-medium">
                                      <span className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                        <MessageSquare className="h-4 w-4" />
                                      </span>
                                      Type: <span className="font-bold text-slate-900 capitalize">{interview.interview_type || interview.job_title || 'Interview Session'}</span>
                                    </p>
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-3 mt-4 md:mt-0">
                                  {interview.meeting_link ? (
                                    <a
                                      href={interview.meeting_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-8 py-3.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-black rounded-2xl transition-all shadow-lg shadow-cyan-600/30 flex items-center gap-3 active:scale-95 group"
                                    >
                                      <Video className="w-5 h-5 group-hover:animate-pulse" />
                                      JOIN SESSION
                                    </a>
                                  ) : (
                                    <div className="px-6 py-3.5 bg-slate-100 text-slate-400 text-sm font-bold rounded-2xl border border-slate-200 flex items-center gap-2 opacity-60">
                                      <Video className="w-4 h-4" />
                                      LINK PENDING
                                    </div>
                                  )}
                                  <button className="p-3.5 hover:bg-slate-50 rounded-2xl border border-slate-100 transition-colors text-slate-400 hover:text-slate-600">
                                    <MoreHorizontal className="h-5 w-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'language-classes' && (
              <LanguageClassesManager role="student" />
            )}



            {/* Progress Details Modal */}
            {showProgressModal && dashboardData?.user && (
              <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full flex flex-col scale-100 animate-scaleIn">
                  <div className="p-6 md:p-8 border-b border-gray-100 flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Application Progress</h2>
                      <p className="text-slate-500 mt-1 text-sm">Your journey through the recruitment pipeline</p>
                    </div>
                    <button
                      onClick={() => setShowProgressModal(false)}
                      className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-red-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="p-6 md:p-8 overflow-y-auto max-h-[70vh]">
                    {(() => {
                      const stages = [
                        'Language Learning',
                        'Document Submitted',
                        'Screening Waiting',
                        'Screening Passed',
                        'Interview Waiting',
                        'Interview Scheduled',
                        'Interview attended',
                        'Offer Received',
                        'Visa submitted',
                        'Visa Granted',
                        'Migrated'
                      ];
                      const currentStage = dashboardData.user.progress_stage || 'Language Learning';
                      const isRejected = currentStage === 'Rejected';
                      const currentIndex = isRejected ? -1 : stages.indexOf(currentStage);

                      return (
                        <div className="relative border-l-2 border-slate-200 ml-4 space-y-8 pl-8 py-2">
                          {isRejected ? (
                            <div className="relative flex items-center">
                              <div className="absolute -left-[41px] h-5 w-5 rounded-full bg-red-500 border-4 border-white shadow-sm" />
                              <div>
                                <h4 className="text-lg font-bold text-red-600">Application Rejected</h4>
                                <p className="text-sm text-slate-500">Your application did not proceed further.</p>
                              </div>
                            </div>
                          ) : (
                            stages.map((stage, idx) => {
                              const isPast = idx < currentIndex;
                              const isCurrent = idx === currentIndex;

                              let bgClass = "bg-slate-200";
                              let textClass = "text-slate-400";
                              let desc = "";

                              if (isPast) {
                                bgClass = "bg-emerald-500";
                                textClass = "text-emerald-700 font-bold";
                                desc = "Completed";
                              } else if (isCurrent) {
                                bgClass = "bg-cyan-600 animate-pulse";
                                textClass = "text-cyan-700 font-extrabold text-lg";
                                desc = "In Progress";
                                if (['Interview Waiting', 'Interview Scheduled', 'Interview attended', 'Offer Received'].includes(stage) && dashboardData.user.interview_company) {
                                  desc += ` at ${dashboardData.user.interview_company}`;
                                }
                              } else {
                                desc = "Upcoming";
                              }

                              return (
                                <div key={stage} className="relative flex items-center">
                                  <div className={`absolute -left-[41px] h-5 w-5 rounded-full border-4 border-white shadow-sm transition-all ${bgClass}`} />
                                  <div>
                                    <h4 className={`${textClass} transition-colors`}>{stage}</h4>
                                    <p className="text-sm text-slate-500 mt-0.5">{desc}</p>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="max-w-2xl mx-auto animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-slate-400" />
                    Account Settings
                  </h3>

                  <form onSubmit={handlePasswordReset} className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">New Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                          placeholder="Enter new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {passwordError && (
                      <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {passwordError}
                      </div>
                    )}

                    <div className="pt-4">
                      <button
                        type="submit"
                        className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg hover:shadow-xl"
                      >
                        Update Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </main >
      </div >

      {/* Floating Chat Button - Removed */}




      {
        showSkillModal && (
          <AddSkillModal
            isOpen={showSkillModal}
            onClose={() => setShowSkillModal(false)}
            onSave={handleSkillSave}
          />
        )
      }
    </div >
  );
};

export default StudentDashboard;
